import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaTrash, FaChevronLeft, FaDollarSign } from 'react-icons/fa'; // Added icons
import { StandardURL } from "../App";


const UpdateTrashUnitsPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [newAmount, setNewAmount] = useState("");

    const handleUpdateTrashUnits = () => {
        setIsLoading(true);

        // Get property ID from localStorage
        const propertyid = JSON.parse(localStorage.getItem("propertyIdTrash"));
        const token = localStorage.getItem("token");

        // Basic validation
        if (!propertyid || !token) {
            toast.error("Missing Property ID or authentication token.");
            setIsLoading(false);
            return;
        }

        if (!newAmount || isNaN(Number(newAmount)) || Number(newAmount) <= 0) {
            toast.error("Please enter a valid amount greater than zero.");
            setIsLoading(false);
            return;
        }

        // Create an object containing the new trash units price (feeamount)
        const requestBody = {
            feeamount: Number(newAmount), // Ensure it's sent as a number if possible
        };

        // Send a PATCH request to update trash units price using axios
        axios
            .patch(
                `${StandardURL}/iNyumba/trashfee/updatetrashfee/${propertyid}`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response);
                // navigate(-1) is handled after successful action
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.response || "An error occurred while updating the fee.";
                toast.error(errorMessage);
                console.error("Error updating trash units:", error);
            })
            .finally(() => {
                setIsLoading(false); // Set loading state to false regardless of success/fail
                navigate(-1); // Navigate back to previous page
            });
    };

    const handleNewAmountChange = (e) => {
        setNewAmount(e.target.value);
    };

    const goBack = () => navigate(-1);

    const propertyid = JSON.parse(localStorage.getItem("propertyIdTrash"));

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-white p-8 rounded-xl shadow-2xl border border-gray-200">
                        
                        {/* Header and Back Button */}
                        <div className="mb-8 flex justify-between items-center border-b pb-4">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaTrash className="text-red-500 mr-3" /> Update Trash Fee
                            </h1>
                            <button
                                onClick={goBack}
                                className="text-gray-600 hover:text-blue-600 transition duration-150 flex items-center text-sm"
                            >
                                <FaChevronLeft className="mr-1 w-3 h-3" /> Back
                            </button>
                        </div>
                        
                        {/* Information Text */}
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6 text-yellow-800 text-sm">
                            <p className="font-semibold mb-1">Property ID: <span className="text-yellow-900">{propertyid || 'N/A'}</span></p>
                            <p>Enter the **new flat fee amount** for trash units for this property. This change will affect future trash bill generation.</p>
                        </div>

                        {/* Input Field */}
                        <div className="mb-6">
                            <label htmlFor="newAmount" className="block text-sm font-medium text-gray-700 mb-2">
                                New Trash Fee Amount (e.g., 500)
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                                    <FaDollarSign className="w-5 h-5" />
                                </span>
                                <input
                                    type="number"
                                    id="newAmount"
                                    className="w-full border border-gray-300 rounded-lg py-3 pl-10 pr-4 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition duration-150"
                                    value={newAmount}
                                    onChange={handleNewAmountChange}
                                    placeholder="Enter new amount"
                                    min="0"
                                />
                            </div>
                        </div>

                        {/* Action Button */}
                        <button
                            onClick={handleUpdateTrashUnits}
                            disabled={isLoading || !newAmount || newAmount <= 0}
                            className={`w-full flex items-center justify-center font-bold py-3 px-4 rounded-lg shadow-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 
                                ${isLoading
                                    ? "bg-gray-500 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500"
                                } disabled:opacity-50`}
                        >
                            {isLoading ? (
                                <>
                                    <Oval
                                        height={20}
                                        width={20}
                                        color="white"
                                        visible={true}
                                        ariaLabel="loading-indicator"
                                        secondaryColor="#ffffff"
                                        strokeWidth={5}
                                    />
                                    <span className="ml-2">Updating...</span>
                                </>
                            ) : (
                                "Update Trash Fee Price"
                            )}
                        </button>
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UpdateTrashUnitsPage;